import {computed, type ComputedRef, type Ref, ref} from 'vue'
import {defineStore} from 'pinia'
import type {GeoJSONGeometryCollection} from "ol/format/GeoJSON";
import {
  // filterMetadata,
  filterMetadataExclusive,
  //getFilterEntriesFromMultiValue,
  getFilterItems
} from "@/stores/filtering";

const geoJSONURL = import.meta.env.VITE_GEO_JSON_DATA_URL;
const metadataURL = import.meta.env.VITE_METADATA_DATA_URL;

const metadataPropertyId = 'gendib_pop_id';

export const useMetadataStore = defineStore('metadataStore', () => {
  const metadataDictionary: Ref<metadataObject> = ref([])
  const geoJsonCollection: Ref<GeoJSONFeatureCollection> = ref({ type: 'FeatureCollection', features: [] });


  const selectionId = ref('')
  const featureRangeSelection = ref(100);

  const filterPropertyKeys = ref(['species_group', 'species_scientific', 'marker_types','canton','data_set_published','swiss_data']);
  const filterMap = ref(new Map<string, FilterItem[]>())

  const activeFilters = ref(new Map<string, string[] | null>);

  const hasActiveFilters = computed(() => {

    for (const [key, value] of activeFilters.value.entries()) {
      if (value !== null) {
        return true;
      }
    }

    return false;
  });

  const exclusiveFiltering = ref(true);

/*
  const activeFilterProps = computed(() => {
    const allProps = Array.from(activeFilters.value.values());
    return allProps.filter((value) => value !== null);
  })
*/
  const speciesGroupMap = computed(() => {
    const map = new Map();
    Object.values(metadataDictionary.value).forEach(metadata => {
      if (metadata.gendib_pop_id && metadata.species_group) {
        map.set(metadata.gendib_pop_id, metadata.species_group);
      }
    });
    return map;
  });

  const showSwissDataOnly = ref('yes');
  const showDataSetPublishedOnly = ref('no');


  const selectedGeoJSON = computed(() => {
    return geoJsonCollection.value.features?.find(feature => feature.properties[metadataPropertyId] === selectionId.value);
  })

  const selectedMetadata = computed(() => {

    return metadataDictionary.value[selectionId.value] || null;


  })


  function selectRecord(id: string) {
    selectionId.value = id
  }

  function initializeFilters() {
    filterPropertyKeys.value.forEach((key) => {
      if (key === 'swiss_data') {
        activeFilters.value.set(key, showSwissDataOnly.value === 'yes' ? ['yes'] : null);
      } else {
        activeFilters.value.set(key, null);
      }
    });
  }
  initializeFilters();

  const fetchMetadata = async () => {
    try {
      const response = await fetch(metadataURL);
      metadataDictionary.value = await response.json();
      // console.log(`loaded metadata ${JSON.stringify(metadataDictionary.value)}`);

      // // initialize the filters with null
      // filterPropertyKeys.value.forEach((key) => {
      //   activeFilters.value.set(key, null);
      // });

      const metadataValues: MetadataObject[] = Object.values(metadataDictionary.value);

      updateFilterMap(filterPropertyKeys.value, metadataValues);
    } catch (e) {
      console.log(`Error fetching the url: ${metadataURL}`);
      console.error(e);
    }

  }

  const fetchGeoJSON = async () => {
    try {
      const response = await fetch(geoJSONURL);
      geoJsonCollection.value = await response.json();
      // console.log(`loaded geojson ${JSON.stringify(geoJsonCollection.value)}`);
    } catch (e) {
      console.log(`Error fetching the url: ${geoJSONURL}`);
      console.error(e);
    }
  }

  const filteredFeatures = computed(() => {
    const filtered: GeoJSONGeometryCollection = geoJsonCollection.value;

    if (!filtered?.features) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }

    let filteredGeoFeatures = filtered.features;

    if (filteredMetadata.value) {
      // only create a subset if there is a filtered metadata subset, otherwise use all features from geoJsonCollection
      const metadataValues: MetadataObject[] = filteredMetadata.value;
      const filterIds = metadataValues.map((metadataObject) => metadataObject[metadataPropertyId]);

      const subset = [];
      for (let i = 0; i < filtered.features.length; i++) {
        const feat = filtered.features[i];
        if (filterIds.includes(feat.properties[metadataPropertyId])) {
          // only add the points to the subset which are part of the filteredMetadata ids
          subset.push(feat);
        }
      }

      filteredGeoFeatures = subset;
    }

    // If showSwissDataOnly is true, filter for only Swiss data
    // if (showSwissDataOnly.value) {
    //   return {
    //     type: 'FeatureCollection',
    //     features: geoJsonCollection.value.features.filter(feature => feature.properties.swiss_data === 'yes'),
    //   };
    // }

    return {
      type: 'FeatureCollection',
      features: filteredGeoFeatures,
    };
  })

  const setFeatureRangeSelection = (newRange: number) => {
    featureRangeSelection.value = newRange;
  }

  const updateFilterMap = (filterProperties: string[], metadataValues: MetadataObject[]) => {

    filterProperties.forEach((key) => {
      const filterItems = getFilterItems(metadataValues, key);
      filterMap.value.set(key, filterItems);
    });

    console.log('updateFilterMap');
    console.log(filterMap.value.entries());
  }


  /**
   * if there aren't any filters applied return null
   * to so the full list of can be used. Meaning when filterMetadata is null, show all metadata entries
   *
   */
  const filteredMetadata: ComputedRef<MetadataObject[] | null> = computed(() => {
    const activeFiltersObject = Object.fromEntries(activeFilters.value);
    console.log("Active filters with values:", activeFiltersObject);
    if (!hasActiveFilters.value) {
      console.log("I am here")
      return null;
    }

    const metadataValues = Object.values(metadataDictionary.value);

    // if (exclusiveFiltering.value) {
    //   return filterMetadataExclusive(metadataValues, activeFilters.value)
    // }

    return filterMetadataExclusive(metadataValues, activeFilters.value);
  });

  const changeFilter = (filterItem: ActiveFilterItem) => {

    const { filterKey, propKey } : { filterKey: string, propKey: string[] | null } = filterItem;
    const filterPropertyValue = propKey && propKey.length > 0 ? propKey : null;
    activeFilters.value.set(filterKey, filterPropertyValue);

    console.log(`After change: Active filter for ${filterKey} is`, activeFilters.value.get(filterKey));

    if (exclusiveFiltering.value) {
      // only update the filter items when exclusive filtering is active
      const metadataValues: MetadataObject[] = filteredMetadata.value ? filteredMetadata.value : Object.values(metadataDictionary.value);
      updateFilterMap(filterPropertyKeys.value, metadataValues);
    }
  }

  const changeExclusiveFiltering = (exclusive: boolean) => {
    exclusiveFiltering.value = exclusive;

    // when the exclusive filtering toggle changes,
    // always reset the filter items from the full list of metadata entries
    const metadataValues: MetadataObject[] = Object.values(metadataDictionary.value); //exclusive ? filteredMetadata.value : Object.values(metadataDictionary.value);
    updateFilterMap(filterPropertyKeys.value, metadataValues);
  }

  return {
    geoJsonCollection, metadataDictionary,
    filterMap, filterPropertyKeys,
    selectRecord, selectedGeoJSON, selectedMetadata,
    fetchMetadata, fetchGeoJSON,
    setFeatureRangeSelection,
    changeFilter, filteredFeatures, filteredMetadata,
    exclusiveFiltering, changeExclusiveFiltering,
    showSwissDataOnly,
    showDataSetPublishedOnly,speciesGroupMap
  }
})
